// ═══════════════════════════════════════════════════════════
// SVG DEVICE ICONS — Inline SVG for crisp rendering at any zoom
// ═══════════════════════════════════════════════════════════

export default function DeviceIcon({ type, size = 28, color = 'var(--color-infra)' }: {
  type: string
  size?: number
  color?: string
}) {
  const s = size
  const h = s / 2
  const icons: Record<string, React.ReactNode> = {
    dome: (
      <g>
        <circle cx={h} cy={h} r={h - 2} fill={color} opacity={0.15} stroke={color} strokeWidth={1.5} />
        <circle cx={h} cy={h} r={h * 0.4} fill={color} />
        <circle cx={h} cy={h} r={h * 0.2} fill="#fff" />
      </g>
    ),
    bullet: (
      <g>
        <rect x={2} y={h - 5} width={s - 4} height={10} rx={3} fill={color} opacity={0.15} stroke={color} strokeWidth={1.5} />
        <circle cx={s - 8} cy={h} r={4} fill={color} />
        <circle cx={s - 8} cy={h} r={2} fill="#fff" />
      </g>
    ),
    pano: (
      <g>
        <circle cx={h} cy={h} r={h - 2} fill={color} opacity={0.15} stroke={color} strokeWidth={1.5} strokeDasharray="3 2" />
        <circle cx={h} cy={h} r={h * 0.5} fill={color} />
        <circle cx={h} cy={h} r={2} fill="#fff" />
      </g>
    ),
    switch: (
      <g>
        <rect x={1} y={h - 7} width={s - 2} height={14} rx={2} fill={color} opacity={0.15} stroke={color} strokeWidth={1.5} />
        {[0.25, 0.4, 0.55, 0.7].map((p, i) => (
          <rect key={i} x={s * p - 1} y={h - 3} width={3} height={6} rx={0.5} fill={color} />
        ))}
      </g>
    ),
    ap: (
      <g>
        <circle cx={h} cy={h} r={h - 2} fill={color} opacity={0.1} stroke={color} strokeWidth={1.5} />
        <path d={`M${h - 6},${h + 2} Q${h},${h - 8} ${h + 6},${h + 2}`} fill="none" stroke={color} strokeWidth={1.5} />
        <path d={`M${h - 4},${h + 2} Q${h},${h - 4} ${h + 4},${h + 2}`} fill="none" stroke={color} strokeWidth={1.5} />
        <circle cx={h} cy={h + 2} r={2} fill={color} />
      </g>
    ),
    reader: (
      <g>
        <rect x={h - 5} y={2} width={10} height={s - 4} rx={2} fill={color} opacity={0.15} stroke={color} strokeWidth={1.5} />
        <rect x={h - 3} y={h - 2} width={6} height={4} rx={1} fill={color} />
        <circle cx={h} cy={8} r={2} fill={color} opacity={0.5} />
      </g>
    ),
    controller: (
      <g>
        <rect x={2} y={2} width={s - 4} height={s - 4} rx={3} fill={color} opacity={0.12} stroke={color} strokeWidth={1.5} />
        <rect x={6} y={6} width={s - 12} height={3} rx={1} fill={color} opacity={0.4} />
        <rect x={6} y={11} width={s - 12} height={3} rx={1} fill={color} opacity={0.4} />
        <circle cx={s - 8} cy={s - 8} r={2.5} fill={color} />
      </g>
    ),
    ups: (
      <g>
        <rect x={2} y={2} width={s - 4} height={s - 4} rx={2} fill={color} opacity={0.12} stroke={color} strokeWidth={1.5} />
        <text x={h} y={h + 4} textAnchor="middle" fontSize={10} fontWeight={800} fill={color}>{'\u26A1'}</text>
      </g>
    ),
    server: (
      <g>
        <rect x={3} y={2} width={s - 6} height={s - 4} rx={2} fill={color} opacity={0.12} stroke={color} strokeWidth={1.5} />
        <rect x={6} y={5} width={s - 12} height={4} rx={1} fill={color} opacity={0.3} />
        <rect x={6} y={11} width={s - 12} height={4} rx={1} fill={color} opacity={0.3} />
        <rect x={6} y={17} width={s - 12} height={4} rx={1} fill={color} opacity={0.3} />
      </g>
    ),
    mdf: (
      <g>
        <rect x={2} y={2} width={s - 4} height={s - 4} rx={3} fill="var(--bg-elevated, #374151)" stroke="var(--border-default, #6b7280)" strokeWidth={1.5} />
        <text x={h} y={h + 3} textAnchor="middle" fontSize={7} fontWeight={800} fill="var(--text-primary, #e5e7eb)" letterSpacing={0.5}>MDF</text>
      </g>
    ),
    idf: (
      <g>
        <rect x={2} y={2} width={s - 4} height={s - 4} rx={3} fill="var(--bg-elevated, #374151)" stroke="var(--border-subtle, #9ca3af)" strokeWidth={1.5} strokeDasharray="4 2" />
        <text x={h} y={h + 3} textAnchor="middle" fontSize={7} fontWeight={800} fill="var(--text-primary, #e5e7eb)" letterSpacing={0.5}>IDF</text>
      </g>
    ),
  }
  return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>{icons[type] || icons.dome}</svg>
}

// ── Icon type detection from catalog products ──

export interface CatalogProduct {
  name?: string | null
  category?: string | null
  custom_name?: string | null
  custom_category?: string | null
}

export function detectIconType(product: CatalogProduct): string {
  const name = (product.name || product.custom_name || '').toLowerCase()
  const category = (product.category || product.custom_category || '').toLowerCase()

  if (category === 'surveillance' || category === 'camera' || category === 'cameras') {
    if (name.includes('bullet')) return 'bullet'
    if (name.includes('pano') || name.includes('360')) return 'pano'
    if (name.includes('ptz')) return 'pano'
    if (name.includes('fisheye')) return 'pano'
    return 'dome'
  }
  if (category === 'networking' || category === 'network') {
    if (name.includes('switch') || name.includes('poe')) return 'switch'
    if (name.includes('ap') || name.includes('access point') || name.includes('u6')) return 'ap'
    if (name.includes('router') || name.includes('gateway') || name.includes('udm')) return 'switch'
    return 'switch'
  }
  if (category === 'access_control' || category === 'access control') {
    if (name.includes('reader') || name.includes('credential')) return 'reader'
    if (name.includes('controller')) return 'controller'
    return 'reader'
  }
  if (category === 'power') return 'ups'
  if (category === 'recording' || category === 'computers') return 'server'
  return 'server'
}

// ── Device type detection from category ──

export function detectDeviceType(product: CatalogProduct): string {
  const name = (product.name || product.custom_name || '').toLowerCase()
  const category = (product.category || product.custom_category || '').toLowerCase()

  if (category === 'surveillance' || category === 'camera' || category === 'cameras') return 'camera'
  if (category === 'networking' || category === 'network') {
    if (name.includes('switch') || name.includes('poe') || name.includes('router') || name.includes('gateway') || name.includes('udm')) return 'switch'
    if (name.includes('ap') || name.includes('access point') || name.includes('u6')) return 'ap'
    return 'switch'
  }
  if (category === 'access_control' || category === 'access control') {
    if (name.includes('controller')) return 'controller'
    return 'reader'
  }
  if (category === 'power') return 'power'
  if (category === 'recording' || category === 'computers') return 'nvr'
  return 'nvr'
}

// ── Color by device type ──

export function colorForType(type: string): string {
  const colors: Record<string, string> = {
    camera: 'var(--color-camera)',
    switch: 'var(--color-networking)',
    ap: 'var(--color-networking)',
    reader: 'var(--color-access)',
    controller: 'var(--color-access)',
    power: 'var(--color-power)',
    nvr: 'var(--color-infra)',
    server: 'var(--color-infra)',
  }
  return colors[type] || 'var(--color-infra)'
}

// ── Auto-label generation (per-type counters) ──

export interface LabelableDevice {
  label: string
  type: string
}

export interface LabelTypeConfig {
  prefix: string
  padding: number
  suffix: string
}

export interface LabelConfig {
  camera: LabelTypeConfig
  switch: LabelTypeConfig
  ap: LabelTypeConfig
  reader: LabelTypeConfig
  controller: LabelTypeConfig
  power: LabelTypeConfig
  nvr: LabelTypeConfig
  server?: LabelTypeConfig
  floorOffset: number
}

export const DEFAULT_LABEL_CONFIG: LabelConfig = {
  camera: { prefix: 'CAM-', padding: 2, suffix: '' },
  switch: { prefix: 'SW-', padding: 2, suffix: '' },
  ap: { prefix: 'AP-', padding: 2, suffix: '' },
  reader: { prefix: 'RDR-', padding: 2, suffix: '' },
  controller: { prefix: 'CTRL-', padding: 2, suffix: '' },
  power: { prefix: 'UPS-', padding: 2, suffix: '' },
  nvr: { prefix: 'NVR-', padding: 2, suffix: '' },
  server: { prefix: 'SVR-', padding: 2, suffix: '' },
  floorOffset: 0,
}

export function generateLabel(type: string, existingDevices: LabelableDevice[], config?: LabelConfig, floorNumber?: number): string {
  const cfg = config || DEFAULT_LABEL_CONFIG
  const typeConfig = (cfg as unknown as Record<string, LabelTypeConfig | number>)[type] as LabelTypeConfig | undefined
  const pfx = typeConfig?.prefix ?? 'DEV-'
  const pad = typeConfig?.padding ?? 2
  const sfx = typeConfig?.suffix ?? ''

  // Calculate floor offset
  const offset = (floorNumber && cfg.floorOffset) ? (floorNumber * cfg.floorOffset) : 0

  // Find existing numbers for this prefix
  const existingNumbers = existingDevices
    .filter(d => d.label.startsWith(pfx))
    .map(d => {
      const numPart = d.label.slice(pfx.length).replace(sfx, '')
      return parseInt(numPart)
    })
    .filter(n => !isNaN(n))

  const baseNumber = offset > 0 ? offset + 1 : 1
  const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : baseNumber
  const numStr = pad > 0 ? String(nextNumber).padStart(pad, '0') : String(nextNumber)
  return `${pfx}${numStr}${sfx}`
}
